/** Rendering a duration, a timestamp and a model id at the length a table has. */

/**
 * Seconds as a duration, carrying only the precision the magnitude earns:
 * `8.4s`, `2m 14s`, `26m`, `1h 12m`. A unit is dropped once it stops mattering,
 * and a zero is never padded out to hold a column. Case latencies and run
 * wall-clocks both read naturally without the caller choosing a format.
 */
export function formatDuration(seconds: number | null): string {
  if (seconds == null) {
    return "—";
  }
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }

  const totalMinutes = Math.round(seconds / 60);
  if (totalMinutes < 60) {
    const wholeMinutes = Math.floor(seconds / 60);
    const remainder = Math.round(seconds % 60);
    // Under ten minutes the seconds still separate two runs.
    if (wholeMinutes < 10 && remainder > 0) {
      return `${wholeMinutes}m ${remainder}s`;
    }
    return `${totalMinutes}m`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
}

/** A timestamp as `Aug 28, 3:04 PM`, dropping the year for the current one. */
export function formatEvalTimestamp(ms: number): string {
  if (Number.isNaN(ms)) {
    return "—";
  }
  const date = new Date(ms);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * A model id with its routing prefix dropped, so the part that differs between
 * runs is the part on screen: `bedrock:us.anthropic.claude-sonnet-5` reads as
 * `claude-sonnet-5`. The dotted segments go for `bedrock` only — elsewhere a dot
 * can belong to the name, and `openai:gpt-4.1` cut at its last dot would read `1`.
 */
export const shortModelName = (modelName: string): string => {
  const colon = modelName.indexOf(":");
  if (colon === -1) {
    return modelName;
  }
  const provider = modelName.slice(0, colon);
  const id = modelName.slice(colon + 1);
  return provider === "bedrock" ? id.slice(id.lastIndexOf(".") + 1) : id;
};
